import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ArchiveRestore,
  FileText,
  History,
  KeyRound,
  Pencil,
  UserCheck,
  UserX,
  Users,
} from 'lucide-react';
import { apiRequest, ApiError, type ListResponse } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { timeAgo } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { RelationPanel } from '../components/shared/RelationPanel';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { StatusBadge } from '../components/people/StatusBadge';
import { OverflowMenu } from '../components/people/OverflowMenu';
import { PortalAccessCard } from '../components/system/PortalAccessCard';
import {
  ManagerDrawer,
  type ManagerDrawerMode,
} from '../components/people/ManagerDrawer';
import { decodeStringArray, type ManagerRecord } from '../components/people/types';
import { type BatchRecord } from '../components/academics/types';

export const Route = createFileRoute('/_authed/people/managers/$managerId')({
  component: ManagerDetailPage,
});

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

interface ManagerBatchRow extends BatchRecord {
  enrolledCount: number;
}

type Tab = 'overview' | 'batches' | 'portal' | 'activity';

const TABS: ReadonlyArray<{ value: Tab; label: string; icon: typeof Users }> = [
  { value: 'overview', label: 'Overview', icon: FileText },
  { value: 'batches', label: 'Batches as nalim', icon: Users },
  { value: 'portal', label: 'Portal access', icon: KeyRound },
  { value: 'activity', label: 'Activity', icon: History },
];

const BATCHES_PAGE_SIZE = 100;

function ManagerDetailPage(): JSX.Element {
  const { managerId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const callerId = useAuthStore((s) => s.user?.id ?? null);
  const canManage = role === 'super_admin' || role === 'academic_admin';
  const isSuperAdmin = role === 'super_admin';

  const [tab, setTab] = useState<Tab>('overview');
  const [editDrawer, setEditDrawer] = useState<ManagerDrawerMode | null>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const managerQuery = useQuery({
    queryKey: ['manager', 'detail', managerId] as const,
    queryFn: ({ signal }) =>
      apiRequest<ManagerRecord>(
        `/api/managers/${encodeURIComponent(managerId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const batchesQuery = useQuery({
    queryKey: ['manager', 'batches', managerId] as const,
    queryFn: ({ signal }) =>
      apiRequest<ListResponse<ManagerBatchRow>>('/api/batches', {
        query: { nalimId: managerId, pageSize: BATCHES_PAGE_SIZE },
        signal,
      }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const manager = managerQuery.data;
  const batches = batchesQuery.data?.items ?? [];
  const activeBatches = useMemo(
    () => batches.filter((b) => b.status === 'active'),
    [batches],
  );
  const studentsUnderOversight = useMemo(
    () => activeBatches.reduce((acc, b) => acc + (b.enrolledCount ?? 0), 0),
    [activeBatches],
  );

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['manager', 'detail', managerId] });
    void queryClient.invalidateQueries({ queryKey: ['manager', 'batches', managerId] });
    void queryClient.invalidateQueries({ queryKey: ['managers'] });
  };

  const archiveMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(`/api/managers/${encodeURIComponent(managerId)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      toast('Nalim archived.', { variant: 'success' });
      invalidate();
    },
    // Surface the 409 batches_active error verbatim and keep the manager active.
    // We deliberately do NOT invalidate or close the drawer on error.
    onError: (err: unknown) => {
      const message =
        err instanceof ApiError ? err.message : 'Could not archive nalim.';
      toast(message, { variant: 'error' });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/managers/${encodeURIComponent(managerId)}/restore`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast('Nalim restored.', { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not restore nalim.', {
        variant: 'error',
      });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/managers/${encodeURIComponent(managerId)}/deactivate`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast(`${manager?.name ?? 'Nalim'} deactivated.`, { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not deactivate nalim.', {
        variant: 'error',
      });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/managers/${encodeURIComponent(managerId)}/reactivate`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast(`${manager?.name ?? 'Nalim'} reactivated.`, { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not reactivate nalim.', {
        variant: 'error',
      });
    },
  });

  const handleArchive = async (): Promise<void> => {
    if (!manager) return;
    const ok = await confirm({
      title: 'Archive nalim?',
      description: `${manager.name} will be moved to archived status and their portal login will be disabled.`,
      confirmLabel: 'Archive',
      variant: 'danger',
    });
    if (!ok) return;
    setOverflowOpen(false);
    archiveMutation.mutate();
  };

  const handleDrawerArchive = async (): Promise<void> => {
    if (!manager) return;
    const ok = await confirm({
      title: 'Archive nalim?',
      description: `${manager.name} will be moved to archived status and their portal login will be disabled.`,
      confirmLabel: 'Archive',
      variant: 'danger',
    });
    if (!ok) return;
    archiveMutation.mutate();
  };

  const handleRestore = async (): Promise<void> => {
    if (!manager) return;
    const ok = await confirm({
      title: 'Restore nalim?',
      description: `${manager.name} will be returned to active status.`,
      confirmLabel: 'Restore',
    });
    if (!ok) return;
    restoreMutation.mutate();
  };

  const handleDeactivate = async (): Promise<void> => {
    if (!manager) return;
    const ok = await confirm({
      title: `Deactivate ${manager.name}?`,
      description:
        'Their portal login will be disabled immediately and all active sessions ended. They stay on the active roster — use Archive to file them away instead.',
      confirmLabel: 'Deactivate',
      variant: 'danger',
    });
    if (!ok) return;
    setOverflowOpen(false);
    deactivateMutation.mutate();
  };

  const handleReactivate = async (): Promise<void> => {
    if (!manager) return;
    const ok = await confirm({
      title: `Reactivate ${manager.name}?`,
      description: `${manager.name} will be returned to active status.`,
      confirmLabel: 'Reactivate',
    });
    if (!ok) return;
    reactivateMutation.mutate();
  };

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!manager) return [];
    return [
      {
        label: 'Active batches',
        value: activeBatches.length,
        onClick: () => setTab('batches'),
      },
      {
        label: 'Students under oversight',
        value: studentsUnderOversight,
        onClick: () => setTab('batches'),
      },
      {
        label: 'Joined',
        value: timeAgo(manager.createdAt),
      },
      {
        label: 'Portal',
        value: !manager.userId
          ? 'Not invited'
          : manager.status === 'inactive'
            ? 'Deactivated'
            : manager.status === 'archived'
              ? 'Disabled'
              : 'Active',
        onClick: () => setTab('portal'),
      },
    ];
  }, [manager, activeBatches.length, studentsUnderOversight]);

  const archived = !!manager?.deletedAt || manager?.status === 'archived';
  const isSelfLinked = !!manager?.userId && manager.userId === callerId;
  const canDeactivate =
    isSuperAdmin &&
    !archived &&
    manager?.status === 'active' &&
    !!manager?.userId &&
    !isSelfLinked;
  const canReactivate =
    isSuperAdmin && !archived && manager?.status === 'inactive';

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={managerQuery.error}
        title="Nalim"
        onRetry={() => managerQuery.refetch()}
      >
        {managerQuery.isLoading || !manager ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={manager.name}
              sublabel={
                <>
                  <span className="font-medium uppercase tracking-wide text-[10px] text-[color:var(--color-text-muted)]">
                    Nalim
                  </span>
                  <span aria-hidden="true">·</span>
                  <code className="rounded bg-[color:var(--color-surface)] px-1.5 py-0.5 font-mono text-[11px] text-[color:var(--color-text-muted)]">
                    {manager.employeeId}
                  </code>
                  <span aria-hidden="true">·</span>
                  <span>{manager.scope ?? 'Unscoped'}</span>
                </>
              }
              statusChip={<StatusBadge status={manager.status} />}
              backTo="/people/managers"
              actions={
                canManage ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setEditDrawer({ kind: 'edit', id: manager.id })}
                    >
                      Edit
                    </Button>
                    {archived ? (
                      <Button
                        size="sm"
                        icon={<ArchiveRestore size={14} aria-hidden="true" />}
                        onClick={() => {
                          void handleRestore();
                        }}
                        loading={restoreMutation.isPending}
                      >
                        Restore
                      </Button>
                    ) : null}
                    {canReactivate ? (
                      <Button
                        size="sm"
                        icon={<UserCheck size={14} aria-hidden="true" />}
                        onClick={() => {
                          void handleReactivate();
                        }}
                        loading={reactivateMutation.isPending}
                      >
                        Reactivate
                      </Button>
                    ) : null}
                    {canDeactivate ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<UserX size={14} aria-hidden="true" />}
                        className="text-danger hover:bg-[color:var(--color-danger)]/10"
                        onClick={() => {
                          void handleDeactivate();
                        }}
                        loading={deactivateMutation.isPending}
                      >
                        Deactivate
                      </Button>
                    ) : null}
                    {!archived ? (
                      <OverflowMenu
                        open={overflowOpen}
                        onOpenChange={setOverflowOpen}
                        pending={archiveMutation.isPending}
                        onArchive={() => {
                          void handleArchive();
                        }}
                      />
                    ) : null}
                  </>
                ) : null
              }
            />

            <KpiStrip items={kpiItems} />

            <div
              role="tablist"
              aria-label="Nalim sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TABS.map(({ value, label, icon: Icon }) => {
                const active = tab === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(value)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    <Icon size={14} aria-hidden="true" />
                    {label}
                  </button>
                );
              })}
            </div>

            {tab === 'overview' ? <OverviewTab manager={manager} /> : null}
            {tab === 'batches' ? (
              <BatchesTab
                batches={batches}
                isLoading={batchesQuery.isLoading && !batchesQuery.data}
                error={
                  batchesQuery.isError
                    ? batchesQuery.error instanceof ApiError
                      ? batchesQuery.error.message
                      : 'Could not load batches.'
                    : null
                }
                onRetry={() => {
                  void batchesQuery.refetch();
                }}
              />
            ) : null}
            {tab === 'portal' ? <PortalTab manager={manager} onChanged={invalidate} /> : null}
            {tab === 'activity' ? (
              <ActivityPanel
                targetType="manager"
                targetId={manager.id}
                title="Activity"
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      {canManage && manager ? (
        <ManagerDrawer
          mode={editDrawer}
          onClose={() => setEditDrawer(null)}
          initial={manager}
          isLoadingInitial={false}
          loadError={null}
          showArchive={false}
          onPortalChange={invalidate}
          onSaved={() => {
            setEditDrawer(null);
            invalidate();
            toast('Nalim saved.', { variant: 'success' });
          }}
          onArchive={async () => {
            await handleDrawerArchive();
            setEditDrawer(null);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewTab({ manager }: { manager: ManagerRecord }): JSX.Element {
  const qualifications = decodeStringArray(manager.qualifications);
  const languages = decodeStringArray(manager.languages);
  const programs = decodeStringArray(manager.canTeachPrograms);

  return (
    <section className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Identity
        </h3>
        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <DetailRow
            label="Employee ID"
            value={<code className="font-mono text-xs">{manager.employeeId}</code>}
          />
          <DetailRow label="Date of birth" value={manager.dateOfBirth || '—'} />
          <DetailRow
            label="Gender"
            value={<span className="capitalize">{manager.gender}</span>}
          />
          <DetailRow label="Status" value={<StatusBadge status={manager.status} />} />
          <DetailRow label="Scope" value={manager.scope ?? 'Unscoped'} />
        </dl>
      </div>
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Contact
        </h3>
        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <DetailRow label="Email" value={manager.email} />
          <DetailRow label="Phone" value={manager.contact} />
          <DetailRow
            label="Address"
            value={
              <span className="whitespace-pre-wrap text-[color:var(--color-text)]">
                {manager.address}
              </span>
            }
          />
        </dl>
      </div>
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5 lg:col-span-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Profile
        </h3>
        <dl className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <DetailRow
            label="Qualifications"
            value={
              qualifications.length === 0 ? '—' : <ChipList items={qualifications} />
            }
          />
          <DetailRow
            label="Languages"
            value={languages.length === 0 ? '—' : <ChipList items={languages} />}
          />
          <DetailRow
            label="Can teach"
            value={programs.length === 0 ? '—' : <ChipList items={programs} />}
          />
          {manager.bio ? (
            <div className="sm:col-span-2 flex flex-col gap-1 border-b border-[color:var(--color-border)] pb-2 last:border-b-0 last:pb-0">
              <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Bio
              </dt>
              <dd className="text-sm text-[color:var(--color-text)] whitespace-pre-wrap">
                {manager.bio}
              </dd>
            </div>
          ) : null}
        </dl>
      </div>
    </section>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[color:var(--color-border)] pb-2 last:border-b-0 last:pb-0">
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="text-sm text-[color:var(--color-text)] text-right">{value}</dd>
    </div>
  );
}

function ChipList({ items }: { items: ReadonlyArray<string> }): JSX.Element {
  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {items.map((item) => (
        <span
          key={item}
          className="inline-flex items-center rounded-full bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] text-[color:var(--color-text)]"
        >
          {item}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Batches tab
// ---------------------------------------------------------------------------

interface BatchesTabProps {
  batches: ManagerBatchRow[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}

function BatchesTab({ batches, isLoading, error, onRetry }: BatchesTabProps): JSX.Element {
  return (
    <RelationPanel
      title="Batches as nalim"
      count={batches.length}
      description="Batches this nalim looks after."
      loading={isLoading}
      error={error ? { message: error, onRetry } : null}
      isEmpty={!isLoading && batches.length === 0}
      empty={{ copy: 'Not assigned as nalim on any batches yet.' }}
    >
      <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)] text-left text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
            <tr>
              <th className="px-4 py-2">Batch</th>
              <th className="px-4 py-2">Mode</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Enrolled</th>
              <th className="px-4 py-2 text-right">Open</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => (
              <tr
                key={b.id}
                className="border-t border-[color:var(--color-border)] hover:bg-[color:var(--color-surface)]"
              >
                <td className="px-4 py-2 align-middle font-medium text-[color:var(--color-text)]">
                  {b.name}
                </td>
                <td className="px-4 py-2 align-middle text-xs capitalize text-[color:var(--color-text-muted)]">
                  {b.mode}
                </td>
                <td className="px-4 py-2 align-middle">
                  <BatchStatusChip status={b.status} />
                </td>
                <td className="px-4 py-2 align-middle text-right text-xs tabular-nums text-[color:var(--color-text-muted)]">
                  {b.enrolledCount}
                  {b.capacity > 0 ? (
                    <span className="text-[color:var(--color-text-muted)]">
                      {' '}
                      / {b.capacity}
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-2 align-middle text-right">
                  <Link
                    to="/academics/batches/$batchId"
                    params={{ batchId: b.id }}
                    className="text-xs font-medium text-gold-700 hover:underline"
                  >
                    Open batch
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </RelationPanel>
  );
}

function BatchStatusChip({ status }: { status: string }): JSX.Element {
  const tone =
    status === 'active'
      ? 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]'
      : status === 'draft'
        ? 'bg-gold-50 text-gold-700 dark:bg-gold-700/20'
        : status === 'completed'
          ? 'bg-[color:var(--color-surface)] text-[color:var(--color-text)]'
          : 'bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]';
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize',
        tone,
      )}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Portal access tab
// ---------------------------------------------------------------------------

function PortalTab({
  manager,
  onChanged,
}: {
  manager: ManagerRecord;
  onChanged: () => void;
}): JSX.Element {
  return (
    <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
      <PortalAccessCard
        profileType="manager"
        profileId={manager.id}
        profileName={manager.name}
        profileEmail={manager.email}
        userId={manager.userId}
        onChange={onChanged}
      />
    </section>
  );
}
